import type { MarketingReport } from '../types'

const STORAGE_KEY = 'entertainment_marketing_ai_history'
const MAX_HISTORY_ITEMS = 20

export interface HistoryItem {
  id: string
  title: string
  mediaType: 'tv' | 'movie'
  createdAt: string
  report: MarketingReport
}

class HistoryService {
  private getHistory(): HistoryItem[] {
    try {
      const stored = localStorage.getItem(STORAGE_KEY)
      if (!stored) return []
      return JSON.parse(stored) as HistoryItem[]
    } catch (error) {
      console.error('Failed to read history from localStorage:', error)
      return []
    }
  }

  private setHistory(history: HistoryItem[]): void {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(history))
    } catch (error) {
      console.error('Failed to save history to localStorage:', error)
    }
  }

  saveReport(report: MarketingReport): void {
    const history = this.getHistory()

    const historyItem: HistoryItem = {
      id: report.id,
      title: report.showInfo.title,
      mediaType: report.mediaType,
      createdAt: report.createdAt,
      report,
    }

    // Check if this report already exists (by ID)
    const existingIndex = history.findIndex(item => item.id === report.id)
    if (existingIndex !== -1) {
      // Update existing
      history[existingIndex] = historyItem
    } else {
      // Add new at the beginning
      history.unshift(historyItem)
    }

    // Trim to max items
    if (history.length > MAX_HISTORY_ITEMS) {
      history.splice(MAX_HISTORY_ITEMS)
    }

    this.setHistory(history)
  }

  loadReport(id: string): MarketingReport | null {
    const history = this.getHistory()
    const item = history.find(h => h.id === id)
    return item?.report ?? null
  }

  deleteReport(id: string): void {
    const history = this.getHistory()
    const filtered = history.filter(item => item.id !== id)
    this.setHistory(filtered)
  }

  getAllHistory(): HistoryItem[] {
    return this.getHistory()
  }

  clearHistory(): void {
    this.setHistory([])
  }

  getHistoryCount(): number {
    return this.getHistory().length
  }

  exportHistory(): string {
    const history = this.getHistory()
    return JSON.stringify(history, null, 2)
  }

  importHistory(jsonString: string): boolean {
    try {
      const imported = JSON.parse(jsonString) as HistoryItem[]
      if (!Array.isArray(imported)) {
        throw new Error('Invalid history format')
      }
      this.setHistory(imported.slice(0, MAX_HISTORY_ITEMS))
      return true
    } catch (error) {
      console.error('Failed to import history:', error)
      return false
    }
  }
}

export const historyService = new HistoryService()
